import { LitElement, html, css, nothing } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { animate } from 'motion';

// ─────────────────────────────────────────────────────────────────────────────
//  rd-tabs  ·  Fully-featured tabbed control
//
//  Variants  : line (default) | pills | enclosed | soft | unstyled
//  Position  : top (default)  | bottom | left | right
//  Sizes     : small | medium (default) | large
//  Features  : animated indicator, closable tabs, badges, icons,
//              keyboard nav (←→↑↓ Home End), ARIA, lazy panels,
//              stretch layout, overflow scroll, disabled tabs,
//              custom color accent, tab-change / tab-close events
// ─────────────────────────────────────────────────────────────────────────────

// ─── rd-tabs ─────────────────────────────────────────────────────────────────

export class RDTabs extends LitElement {
    static properties = {
        active: { type: String, reflect: true },        // active tab key
        variant: { type: String, reflect: true },        // line | pills | enclosed | soft | unstyled
        position: { type: String, reflect: true },        // top | bottom | left | right
        size: { type: String, reflect: true },        // small | medium | large
        stretch: { type: Boolean, reflect: true },       // tabs fill width / height
        color: { type: String, reflect: true },        // primary | success | warning | danger
        animated: { type: Boolean, reflect: true },       // animate panel transitions
        lazy: { type: Boolean, reflect: true },       // only render active panel DOM
        _tabs: { state: true },                        // internal tab metadata list
    };

    static styles = css`
    /* ── Host ─────────────────────────────────────────────────────── */
    :host {
      display: flex;
      flex-direction: column;
      font-family: var(--font-family, system-ui, -apple-system, sans-serif);
      color: var(--text-main, #1f2328);
      --tab-accent:   var(--primary-color,   #3b82f6);
      --tab-radius:   var(--border-radius,   6px);
      --tab-speed:    var(--transition-speed, 0.2s);
      --tab-border:   var(--border-color,     #e2e8f0);
      --tab-bg:       var(--bg-panel,         #ffffff);
      --tab-soft-bg:  var(--bg-subtle,        #f1f5f9);
    }

    /* Accent colour overrides */
    :host([color="success"])  { --tab-accent: var(--success-color, #22c55e); }
    :host([color="warning"])  { --tab-accent: var(--warning-color, #f59e0b); }
    :host([color="danger"])   { --tab-accent: var(--danger-color,  #ef4444); }
    :host([color="neutral"])  { --tab-accent: var(--text-secondary, #64748b); }

    /* Vertical layout when position = left | right */
    :host([position="left"]),
    :host([position="right"]) {
      flex-direction: row;
    }
    :host([position="right"]) {
      flex-direction: row-reverse;
    }
    :host([position="bottom"]) {
      flex-direction: column-reverse;
    }

    /* ── Tab list ──────────────────────────────────────────────────── */
    .tab-list {
      display: flex;
      flex-direction: row;
      position: relative;
      overflow-x: auto;
      overflow-y: hidden;
      scrollbar-width: none;
      flex-shrink: 0;
      -webkit-overflow-scrolling: touch;
    }

    .tab-list::-webkit-scrollbar { display: none; }

    :host([position="left"]) .tab-list,
    :host([position="right"]) .tab-list {
      flex-direction: column;
      overflow-x: hidden;
      overflow-y: auto;
      min-width: 0;
    }

    :host([stretch]) .tab-list {
      width: 100%;
    }

    /* ── Variant: line (default) ────────────────────────────────────── */
    :host(:not([variant])) .tab-list,
    :host([variant="line"]) .tab-list {
      border-bottom: 2px solid var(--tab-border);
    }
    :host([position="bottom"]:not([variant])) .tab-list,
    :host([position="bottom"][variant="line"]) .tab-list {
      border-bottom: none;
      border-top: 2px solid var(--tab-border);
    }
    :host([position="left"]:not([variant])) .tab-list,
    :host([position="left"][variant="line"]) .tab-list {
      border-bottom: none;
      border-right: 2px solid var(--tab-border);
    }
    :host([position="right"]:not([variant])) .tab-list,
    :host([position="right"][variant="line"]) .tab-list {
      border-bottom: none;
      border-left: 2px solid var(--tab-border);
    }

    /* ── Variant: enclosed ─────────────────────────────────────────── */
    :host([variant="enclosed"]) .tab-list {
      background: var(--tab-soft-bg);
      border: 1px solid var(--tab-border);
      border-bottom: none;
      border-radius: var(--tab-radius) var(--tab-radius) 0 0;
      padding: 0 4px;
      gap: 2px;
    }
    :host([position="bottom"][variant="enclosed"]) .tab-list {
      border-bottom: 1px solid var(--tab-border);
      border-top: none;
      border-radius: 0 0 var(--tab-radius) var(--tab-radius);
    }

    /* ── Variant: soft ─────────────────────────────────────────────── */
    :host([variant="soft"]) .tab-list {
      background: var(--tab-soft-bg);
      border-radius: calc(var(--tab-radius) * 1.5);
      padding: 4px;
      gap: 2px;
    }

    /* ── Variant: pills ─────────────────────────────────────────────── */
    :host([variant="pills"]) .tab-list {
      gap: 4px;
      padding: 0 0 4px;
    }
    :host([position="left"][variant="pills"]) .tab-list,
    :host([position="right"][variant="pills"]) .tab-list {
      padding: 0 4px 0 0;
    }

    /* ── Sliding indicator ─────────────────────────────────────────── */
    .indicator {
      position: absolute;
      bottom: -2px;
      left: 0;
      height: 2px;
      background: var(--tab-accent);
      border-radius: 2px 2px 0 0;
      transition: left var(--tab-speed) cubic-bezier(0.4,0,0.2,1),
                  width var(--tab-speed) cubic-bezier(0.4,0,0.2,1),
                  top var(--tab-speed) cubic-bezier(0.4,0,0.2,1),
                  height var(--tab-speed) cubic-bezier(0.4,0,0.2,1);
      pointer-events: none;
      z-index: 1;
    }

    :host([position="bottom"]:not([variant])) .indicator,
    :host([position="bottom"][variant="line"]) .indicator {
      bottom: auto;
      top: -2px;
      border-radius: 0 0 2px 2px;
    }
    :host([position="left"]:not([variant])) .indicator,
    :host([position="left"][variant="line"]) .indicator {
      bottom: auto;
      left: auto;
      right: -2px;
      width: 2px;
      height: 0;
      border-radius: 2px 0 0 2px;
    }
    :host([position="right"]:not([variant])) .indicator,
    :host([position="right"][variant="line"]) .indicator {
      bottom: auto;
      left: -2px;
      width: 2px;
      height: 0;
      border-radius: 0 2px 2px 0;
    }

    /* Hide indicator for non-line variants */
    :host([variant="pills"]) .indicator,
    :host([variant="enclosed"]) .indicator,
    :host([variant="soft"]) .indicator,
    :host([variant="unstyled"]) .indicator {
      display: none;
    }

    /* ── Tab buttons ───────────────────────────────────────────────── */
    .tab-btn {
      display: inline-flex;
      align-items: center;
      gap: 6px;
      padding: var(--tab-py, 10px) var(--tab-px, 16px);
      border: none;
      background: transparent;
      color: var(--text-secondary, #64748b);
      font-family: inherit;
      font-size: var(--tab-font-size, 13px);
      font-weight: 500;
      white-space: nowrap;
      cursor: pointer;
      position: relative;
      user-select: none;
      transition: color var(--tab-speed) ease,
                  background var(--tab-speed) ease;
      border-radius: 0;
      flex-shrink: 0;
      outline: none;
    }

    /* Size variants */
    :host([size="small"]) .tab-btn  { --tab-py: 6px;  --tab-px: 12px; --tab-font-size: 11px; }
    :host([size="medium"]) .tab-btn { --tab-py: 10px; --tab-px: 16px; --tab-font-size: 13px; }
    :host([size="large"]) .tab-btn  { --tab-py: 13px; --tab-px: 22px; --tab-font-size: 15px; }

    :host([stretch]) .tab-btn {
      flex: 1;
      justify-content: center;
    }

    .tab-btn:hover:not([disabled]) {
      color: var(--text-main, #1f2328);
      background: rgba(0,0,0,0.04);
    }

    .tab-btn:focus-visible {
      outline: 2px solid var(--tab-accent);
      outline-offset: -2px;
    }

    .tab-btn[disabled] {
      opacity: 0.4;
      cursor: not-allowed;
      pointer-events: none;
    }

    /* Active state */
    .tab-btn[aria-selected="true"] {
      color: var(--tab-accent);
    }

    /* ── Variant-specific button styles ─────────────────────────────── */

    /* pills: active gets filled background */
    :host([variant="pills"]) .tab-btn {
      border-radius: 999px;
    }
    :host([variant="pills"]) .tab-btn[aria-selected="true"] {
      background: var(--tab-accent);
      color: #fff;
    }
    :host([variant="pills"]) .tab-btn:hover:not([disabled]):not([aria-selected="true"]) {
      background: color-mix(in srgb, var(--tab-accent) 10%, transparent);
      color: var(--tab-accent);
    }

    /* soft: active gets white card with shadow */
    :host([variant="soft"]) .tab-btn {
      border-radius: calc(var(--tab-radius) * 1.2);
    }
    :host([variant="soft"]) .tab-btn[aria-selected="true"] {
      background: var(--tab-bg);
      color: var(--tab-accent);
      box-shadow: 0 1px 3px rgba(0,0,0,0.1), 0 1px 2px rgba(0,0,0,0.06);
    }
    :host([variant="soft"]) .tab-btn:hover:not([disabled]):not([aria-selected="true"]) {
      background: rgba(255,255,255,0.5);
      color: var(--text-main, #1f2328);
    }

    /* enclosed: active gets flat white card sitting on top of border */
    :host([variant="enclosed"]) .tab-btn {
      padding: 8px 16px;
      margin-top: 4px;
      border-radius: var(--tab-radius) var(--tab-radius) 0 0;
    }
    :host([variant="enclosed"]) .tab-btn[aria-selected="true"] {
      background: var(--tab-bg);
      color: var(--tab-accent);
      border: 1px solid var(--tab-border);
      border-bottom: 2px solid var(--tab-bg);
      margin-bottom: -2px;
      z-index: 1;
    }
    :host([position="bottom"][variant="enclosed"]) .tab-btn {
      margin-top: 0;
      margin-bottom: 4px;
      border-radius: 0 0 var(--tab-radius) var(--tab-radius);
    }
    :host([position="bottom"][variant="enclosed"]) .tab-btn[aria-selected="true"] {
      border-bottom: 1px solid var(--tab-border);
      border-top: 2px solid var(--tab-bg);
      margin-bottom: -2px;
      margin-top: 0;
    }

    /* ── Icons ──────────────────────────────────────────────────────── */
    .tab-icon {
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 1.1em;
      flex-shrink: 0;
      width: 1em;
      height: 1em;
    }

    .tab-icon svg {
      width: 100%;
      height: 100%;
      fill: currentColor;
    }

    /* ── Badge ──────────────────────────────────────────────────────── */
    .tab-badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      min-width: 18px;
      height: 18px;
      padding: 0 5px;
      border-radius: 999px;
      background: var(--tab-accent);
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      line-height: 1;
    }

    :host(:not([variant])) .tab-btn:not([aria-selected="true"]) .tab-badge,
    :host([variant="line"]) .tab-btn:not([aria-selected="true"]) .tab-badge {
      background: var(--text-secondary, #94a3b8);
    }

    /* ── Close button ────────────────────────────────────────────────── */
    .tab-close {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      width: 16px;
      height: 16px;
      border: none;
      background: transparent;
      cursor: pointer;
      color: currentColor;
      border-radius: 4px;
      padding: 0;
      margin-left: 2px;
      opacity: 0.5;
      transition: opacity var(--tab-speed) ease, background var(--tab-speed) ease;
      font-size: 12px;
      line-height: 1;
    }
    .tab-close:hover {
      opacity: 1;
      background: rgba(0,0,0,0.08);
    }
    .tab-close svg {
      width: 10px;
      height: 10px;
      fill: currentColor;
    }

    /* ── Panel wrapper ─────────────────────────────────────────────── */
    .panel-wrap {
      flex: 1;
      position: relative;
      overflow: hidden;
      min-height: 0;
    }

    /* ── Panel ──────────────────────────────────────────────────────── */
    .panel-slot-wrapper {
      display: contents;
    }

    /* ── Enclosed panel border ──────────────────────────────────────── */
    :host([variant="enclosed"]) .panel-wrap {
      border: 1px solid var(--tab-border);
      border-top: none;
      border-radius: 0 0 var(--tab-radius) var(--tab-radius);
    }
    :host([position="bottom"][variant="enclosed"]) .panel-wrap {
      border-top: 1px solid var(--tab-border);
      border-bottom: none;
      border-radius: var(--tab-radius) var(--tab-radius) 0 0;
    }

    /* ── Empty state ─────────────────────────────────────────────────── */
    .empty-state {
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 40px 20px;
      color: var(--text-secondary, #94a3b8);
      font-size: 13px;
      font-style: italic;
    }
  `;

    constructor() {
        super();
        this.active = '';
        this.variant = 'line';
        this.position = 'top';
        this.size = 'medium';
        this.stretch = false;
        this.color = 'primary';
        this.animated = true;
        this.lazy = false;
        this._tabs = [];
        this._everActive = new Set();
        this._indicatorStyle = '';
    }

    // ── Lifecycle ─────────────────────────────────────────────────────────────

    connectedCallback() {
        super.connectedCallback();
        this._onSlotChange();
    }

    firstUpdated() {
        this._updateIndicator();
    }

    updated(changed) {
        if (changed.has('active')) {
            this._everActive.add(this.active);
            this._updateIndicator();
            this._animatePanel();
            this._scrollActiveTabIntoView();
        }
        if (changed.has('variant') || changed.has('position')) {
            this._updateIndicator();
        }
    }

    // ── Slot change observation ────────────────────────────────────────────────

    _onSlotChange() {
        const panels = /** @type {RDTabPanel[]} */ (
            [...this.querySelectorAll(':scope > rd-tab-panel')]
        );

        this._tabs = panels.map(p => ({
            key: p.key || p.getAttribute('key') || '',
            label: p.label || p.getAttribute('label') || '',
            icon: p.icon || p.getAttribute('icon') || '',
            badge: p.badge || p.getAttribute('badge') || '',
            disabled: p.hasAttribute('disabled'),
            closable: p.hasAttribute('closable'),
        }));

        if (!this.active && this._tabs.length) {
            const first = this._tabs.find(t => !t.disabled);
            if (first) this.active = first.key;
        }

        if (this.active) this._everActive.add(this.active);
        this._syncPanels();
        this.requestUpdate();
    }

    _syncPanels() {
        const panels = /** @type {RDTabPanel[]} */ (
            [...this.querySelectorAll(':scope > rd-tab-panel')]
        );
        panels.forEach(p => {
            const k = p.key || p.getAttribute('key') || '';
            p.setAttribute('aria-hidden', k !== this.active ? 'true' : 'false');
            if (!this.lazy || this._everActive.has(k)) {
                p.removeAttribute('hidden-panel');
            } else {
                p.setAttribute('hidden-panel', '');
            }
        });
    }

    // ── Tab selection ──────────────────────────────────────────────────────────

    _selectTab(key) {
        if (key === this.active) return;
        const tab = this._tabs.find(t => t.key === key);
        if (!tab || tab.disabled) return;

        const prev = this.active;
        this.active = key;
        this._syncPanels();

        this.dispatchEvent(new CustomEvent('tab-change', {
            bubbles: true,
            composed: true,
            detail: { key, prev },
        }));
    }

    _closeTab(key, e) {
        e.stopPropagation();
        // Dispatch and let consumer decide whether to remove rd-tab-panel
        this.dispatchEvent(new CustomEvent('tab-close', {
            bubbles: true,
            composed: true,
            detail: { key },
        }));
    }

    // ── Keyboard navigation ────────────────────────────────────────────────────

    _handleKeydown(e) {
        const enabledKeys = this._tabs.filter(t => !t.disabled).map(t => t.key);
        const idx = enabledKeys.indexOf(this.active);
        const isVertical = this.position === 'left' || this.position === 'right';

        const keys = {
            prev: isVertical ? 'ArrowUp' : 'ArrowLeft',
            next: isVertical ? 'ArrowDown' : 'ArrowRight',
        };

        if (e.key === keys.prev) {
            e.preventDefault();
            const prev = enabledKeys[(idx - 1 + enabledKeys.length) % enabledKeys.length];
            this._selectTab(prev);
            this._focusTab(prev);
        } else if (e.key === keys.next) {
            e.preventDefault();
            const next = enabledKeys[(idx + 1) % enabledKeys.length];
            this._selectTab(next);
            this._focusTab(next);
        } else if (e.key === 'Home') {
            e.preventDefault();
            this._selectTab(enabledKeys[0]);
            this._focusTab(enabledKeys[0]);
        } else if (e.key === 'End') {
            e.preventDefault();
            this._selectTab(enabledKeys[enabledKeys.length - 1]);
            this._focusTab(enabledKeys[enabledKeys.length - 1]);
        }
    }

    _focusTab(key) {
        const btn = this.shadowRoot?.querySelector(`[data-key="${CSS.escape(key)}"]`);
        if (btn) /** @type {HTMLElement} */ (btn).focus();
    }

    // ── Indicator animation ────────────────────────────────────────────────────

    _updateIndicator() {
        const indicator = this.shadowRoot?.querySelector('.indicator');
        if (!indicator) return;

        const activeBtn = this.shadowRoot?.querySelector(`[data-key="${CSS.escape(this.active)}"]`);
        if (!activeBtn) return;

        const tabList = this.shadowRoot?.querySelector('.tab-list');
        if (!tabList) return;

        const listRect = tabList.getBoundingClientRect();
        const btnRect = activeBtn.getBoundingClientRect();

        const isVertical = this.position === 'left' || this.position === 'right';

        if (isVertical) {
            const top = btnRect.top - listRect.top;
            const height = btnRect.height;
            indicator.style.top = `${top}px`;
            indicator.style.height = `${height}px`;
        } else {
            const left = btnRect.left - listRect.left + tabList.scrollLeft;
            const width = btnRect.width;
            indicator.style.left = `${left}px`;
            indicator.style.width = `${width}px`;
        }
    }

    _scrollActiveTabIntoView() {
        const btn = this.shadowRoot?.querySelector(`[data-key="${CSS.escape(this.active)}"]`);
        if (btn) btn.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
    }

    // ── Panel animation ────────────────────────────────────────────────────────

    async _animatePanel() {
        if (!this.animated) return;
        const panels = [...this.querySelectorAll(':scope > rd-tab-panel[aria-hidden="false"]')];
        if (!panels.length) return;
        panels.forEach(p => {
            animate(p, { opacity: [0, 1], transform: ['translateY(6px)', 'translateY(0)'] }, {
                duration: 0.2,
                easing: 'ease-out',
            });
        });
    }

    // ── Rendering ─────────────────────────────────────────────────────────────

    _renderIcon(iconHtml) {
        if (!iconHtml) return nothing;
        return html`<span class="tab-icon" aria-hidden="true">${iconHtml.trim().startsWith('<')
            ? html`${unsafeHTML(iconHtml)}`
            : html`<span>${iconHtml}</span>`
            }</span>`;
    }

    _renderBadge(badge) {
        if (!badge && badge !== 0) return nothing;
        return html`<span class="tab-badge">${badge}</span>`;
    }

    _renderCloseBtn(key) {
        return html`
      <button
        class="tab-close"
        tabindex="-1"
        aria-label="Close tab"
        @click="${(e) => this._closeTab(key, e)}"
      >
        <svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>
      </button>
    `;
    }

    render() {
        return html`
      <div
        class="tab-list"
        role="tablist"
        aria-orientation="${(this.position === 'left' || this.position === 'right') ? 'vertical' : 'horizontal'}"
        @keydown="${this._handleKeydown}"
      >
        ${this._tabs.map(tab => html`
          <button
            class="tab-btn"
            role="tab"
            data-key="${tab.key}"
            aria-selected="${tab.key === this.active ? 'true' : 'false'}"
            aria-controls="panel-${tab.key}"
            id="tab-${tab.key}"
            tabindex="${tab.key === this.active ? '0' : '-1'}"
            ?disabled="${tab.disabled}"
            @click="${() => this._selectTab(tab.key)}"
          >
            ${tab.icon ? html`<span class="tab-icon" aria-hidden="true">${tab.icon}</span>` : nothing}
            <span class="tab-label">${tab.label || tab.key}</span>
            ${this._renderBadge(tab.badge)}
            ${tab.closable ? this._renderCloseBtn(tab.key) : nothing}
          </button>
        `)}
        <div class="indicator"></div>
      </div>

      <div class="panel-wrap" role="presentation">
        ${this._tabs.length
                ? html`<slot @slotchange="${this._onSlotChange}"></slot>`
                : html`<div class="empty-state">No tabs yet</div>`
            }
      </div>
    `;
    }
}

customElements.define('rd-tabs', RDTabs);


// ─── rd-tab-panel ─────────────────────────────────────────────────────────────
//  Child element. Declare one per tab inside <rd-tabs>.
//  Attributes: key, label, icon, badge, disabled, closable
// ─────────────────────────────────────────────────────────────────────────────

export class RDTabPanel extends LitElement {
    static properties = {
        key: { type: String, reflect: true },
        label: { type: String, reflect: true },
        icon: { type: String, reflect: true },
        badge: { type: String, reflect: true },
        disabled: { type: Boolean, reflect: true },
        closable: { type: Boolean, reflect: true },
    };

    static styles = css`
    :host {
      display: block;
      padding: var(--tab-panel-padding, 16px);
    }

    :host([aria-hidden="true"]) {
      display: none;
    }

    :host([hidden-panel]) {
      display: none;
    }
  `;

    constructor() {
        super();
        this.key = '';
        this.label = '';
        this.icon = '';
        this.badge = '';
        this.disabled = false;
        this.closable = false;
    }

    render() {
        return html`
      <div
        id="panel-${this.key}"
        role="tabpanel"
        aria-labelledby="tab-${this.key}"
        tabindex="0"
      >
        <slot></slot>
      </div>
    `;
    }
}

customElements.define('rd-tab-panel', RDTabPanel);


// ─── Convenience API ──────────────────────────────────────────────────────────

/**
 * Programmatically switch to a tab by key.
 * @param {string|HTMLElement} tabsElOrId  - <rd-tabs> element or its id
 * @param {string} key                     - tab key to activate
 */
export function setActiveTab(tabsElOrId, key) {
    const el = typeof tabsElOrId === 'string'
        ? document.getElementById(tabsElOrId)
        : tabsElOrId;
    if (el) el.active = key;
}

window.setActiveTab = setActiveTab;


// ═════════════════════════════════════════════════════════════════════════════
//  rd-editor-tabs  ·  VS Code-style editor tab bar
//
//  Features:
//    - Drag-to-reorder (pointer events, smooth ghost)
//    - Pinned tabs (compact, locked position, no close)
//    - Dirty/unsaved indicator (• replaces ×)
//    - Preview tabs (italic, replaced on next preview open)
//    - Overflow scroll with ‹ › arrow buttons
//    - Right-click context menu (Close, Close Others, Close to Right,
//      Close All, Pin/Unpin)
//    - Keyboard nav: ← → Home End, Ctrl+W to close active
//    - ARIA compliant
//    - theme="dark" | "light"
//
//  Usage:
//    <rd-editor-tabs active="main.js">
//      <rd-editor-tab key="main.js"  label="main.js"  icon="js"  dirty></rd-editor-tab>
//      <rd-editor-tab key="index.html" label="index.html" icon="html" pinned></rd-editor-tab>
//      <rd-editor-tab key="style.css"  label="style.css"  icon="css"></rd-editor-tab>
//    </rd-editor-tabs>
// ═════════════════════════════════════════════════════════════════════════════

const FILE_COLORS = {
    js: '#f1c40f', ts: '#3178c6', jsx: '#61dafb', tsx: '#61dafb',
    html: '#e44d26', css: '#264de4', scss: '#cc6699', json: '#cbcb41',
    md: '#519aba', py: '#3572a5', rb: '#cc342d', go: '#00add8',
    rs: '#dea584', vue: '#42b883', svelte: '#ff3e00', txt: '#888',
};

function fileColor(icon = '') {
    return FILE_COLORS[icon.toLowerCase()] || 'var(--tab-accent, #3b82f6)';
}

export class RDEditorTabs extends LitElement {
    static properties = {
        active: { type: String, reflect: true },
        theme: { type: String, reflect: true },  // 'dark' | 'light'
        _tabs: { state: true },
        _order: { state: true },
        _drag: { state: true },  // { key, ghostX, ghostY, overKey, side }
        _ctxMenu: { state: true },  // { key, x, y } | null
        _overflowLeft: { state: true },
        _overflowRight: { state: true },
    };

    static styles = css`
        /* ── Host ─────────────────────────────────────────────────────── */
        :host {
            display: flex;
            flex-direction: column;
            font-family: var(--font-family, 'Segoe UI', system-ui, -apple-system, sans-serif);
            --edt-accent:    var(--primary-color, #3b82f6);
            --edt-speed:     0.12s;
            /* light defaults */
            --edt-bar-bg:    #f3f3f3;
            --edt-tab-bg:    transparent;
            --edt-tab-hover: #e8e8e8;
            --edt-active-bg: #ffffff;
            --edt-border:    #e0e0e0;
            --edt-text:      #6e6e6e;
            --edt-text-act:  #1e1e1e;
            --edt-dirty:     var(--warning-color, #e5a614);
            --edt-panel-bg:  #ffffff;
            --edt-ctx-bg:    #f5f5f5;
            --edt-ctx-hover: #e0e0e0;
            --edt-ctx-text:  #333;
            --edt-sep:       rgba(0,0,0,0.08);
            --edt-pin-icon:  #888;
        }

        :host([theme="dark"]) {
            --edt-bar-bg:    #252526;
            --edt-tab-bg:    transparent;
            --edt-tab-hover: #2d2d2d;
            --edt-active-bg: #1e1e1e;
            --edt-border:    #1e1e1e;
            --edt-text:      #969696;
            --edt-text-act:  #cccccc;
            --edt-dirty:     #e5a614;
            --edt-panel-bg:  #1e1e1e;
            --edt-ctx-bg:    #252526;
            --edt-ctx-hover: #094771;
            --edt-ctx-text:  #cccccc;
            --edt-sep:       rgba(255,255,255,0.07);
            --edt-pin-icon:  #666;
        }

        /* ── Tab bar wrapper ──────────────────────────────────────────── */
        .bar-wrapper {
            display: flex;
            align-items: stretch;
            background: var(--edt-bar-bg);
            border-bottom: 1px solid var(--edt-border);
            height: 35px;
            flex-shrink: 0;
            overflow: hidden;
            user-select: none;
        }

        /* ── Scroll buttons ───────────────────────────────────────────── */
        .scroll-btn {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 24px;
            flex-shrink: 0;
            background: var(--edt-bar-bg);
            border: none;
            cursor: pointer;
            color: var(--edt-text);
            padding: 0;
            transition: background var(--edt-speed), color var(--edt-speed);
            z-index: 2;
        }
        .scroll-btn:hover { background: var(--edt-tab-hover); color: var(--edt-text-act); }
        .scroll-btn[hidden] { display: none; }
        .scroll-btn svg { width: 14px; height: 14px; fill: currentColor; }

        /* ── Tab strip ────────────────────────────────────────────────── */
        .tab-strip {
            display: flex;
            flex: 1;
            overflow-x: auto;
            overflow-y: hidden;
            scrollbar-width: none;
            position: relative;
        }
        .tab-strip::-webkit-scrollbar { display: none; }

        /* ── Individual tab ───────────────────────────────────────────── */
        .tab {
            display: inline-flex;
            align-items: center;
            gap: 5px;
            height: 100%;
            padding: 0 10px 0 11px;
            min-width: 80px;
            max-width: 200px;
            flex-shrink: 0;
            border-right: 1px solid var(--edt-sep);
            cursor: pointer;
            position: relative;
            color: var(--edt-text);
            font-size: 12px;
            font-weight: 400;
            white-space: nowrap;
            overflow: hidden;
            transition: background var(--edt-speed), color var(--edt-speed);
            background: var(--edt-tab-bg);
            box-sizing: border-box;
            -webkit-font-smoothing: antialiased;
            outline: none;
        }

        .tab::before {
            content: '';
            position: absolute;
            top: 0; left: 0; right: 0;
            height: 2px;
            background: transparent;
            transition: background var(--edt-speed);
        }

        .tab:hover {
            background: var(--edt-tab-hover);
            color: var(--edt-text-act);
        }

        .tab[data-active] {
            background: var(--edt-active-bg);
            color: var(--edt-text-act);
            font-weight: 500;
        }
        .tab[data-active]::before {
            background: var(--edt-accent);
        }

        .tab:focus-visible { outline: 2px solid var(--edt-accent); outline-offset: -2px; }

        /* Pinned tabs */
        .tab[data-pinned] {
            min-width: unset;
            max-width: unset;
            padding: 0 8px;
        }
        .tab[data-pinned] .tab-label { display: none; }
        .tab[data-pinned] .tab-close { display: none; }

        /* Preview tabs */
        .tab[data-preview] .tab-label { font-style: italic; }

        /* Dirty state — show ● at rest, swap to × only when hovering the end icon zone */
        .tab[data-dirty] .tab-dirty-dot                        { display: flex; }
        .tab:not([data-dirty]) .tab-dirty-dot                  { display: none; }
        .tab[data-dirty] .tab-close-icon                       { display: none; }
        .tab[data-dirty] .tab-end:hover .tab-close-icon        { display: flex; }
        .tab[data-dirty] .tab-end:hover .tab-dirty-dot         { display: none; }

        /* Drag source ghost */
        .tab[data-dragsource] {
            opacity: 0.35;
        }

        /* Drop indicator */
        .tab[data-dropbefore]::after,
        .tab[data-dropafter]::after {
            content: '';
            position: absolute;
            top: 4px; bottom: 4px;
            width: 2px;
            background: var(--edt-accent);
            border-radius: 2px;
            z-index: 10;
            pointer-events: none;
        }
        .tab[data-dropbefore]::after { left: -1px; }
        .tab[data-dropafter]::after  { right: -1px; }

        /* ── Tab inner parts ──────────────────────────────────────────── */
        .tab-icon {
            flex-shrink: 0;
            width: 12px;
            height: 12px;
            border-radius: 2px;
        }

        .tab-label {
            flex: 1;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            min-width: 0;
        }

        /* Close / dirty dot wrapper */
        .tab-end {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 16px;
            height: 16px;
            flex-shrink: 0;
            margin-left: 2px;
        }

        .tab-close {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 16px;
            height: 16px;
            border-radius: 3px;
            opacity: 0;
            transition: opacity var(--edt-speed), background var(--edt-speed);
        }
        .tab:hover .tab-close,
        .tab[data-active] .tab-close { opacity: 1; }
        .tab-close:hover { background: rgba(128,128,128,0.3); }
        .tab-close svg { width: 10px; height: 10px; fill: currentColor; }

        .tab-dirty-dot {
            align-items: center;
            justify-content: center;
            width: 16px;
            height: 16px;
            color: var(--edt-dirty);
            font-size: 16px;
            line-height: 1;
        }

        /* Pin indicator (small pushpin) */
        .tab-pin-icon {
            width: 10px;
            height: 10px;
            flex-shrink: 0;
            color: var(--edt-pin-icon);
            display: flex;
            align-items: center;
        }
        .tab-pin-icon svg { width: 10px; height: 10px; fill: currentColor; }
        .tab:not([data-pinned]) .tab-pin-icon { display: none; }

        /* ── Drag ghost ───────────────────────────────────────────────── */
        .drag-ghost {
            position: fixed;
            pointer-events: none;
            z-index: 9999;
            border-radius: 4px;
            background: var(--edt-active-bg);
            border: 1px solid var(--edt-accent);
            box-shadow: 0 4px 12px rgba(0,0,0,0.25);
            padding: 0 12px;
            height: 28px;
            display: flex;
            align-items: center;
            gap: 6px;
            font-size: 12px;
            font-weight: 500;
            color: var(--edt-text-act);
            opacity: 0.92;
            transform: rotate(-1.5deg);
            white-space: nowrap;
            transition: none;
        }

        /* ── Panel ────────────────────────────────────────────────────── */
        .panel-wrap {
            flex: 1;
            min-height: 0;
            position: relative;
            background: var(--edt-panel-bg);
        }

        /* ── Context menu ─────────────────────────────────────────────── */
        .ctx-menu {
            position: fixed;
            z-index: 10000;
            background: var(--edt-ctx-bg);
            border: 1px solid var(--edt-border);
            border-radius: 5px;
            box-shadow: 0 6px 20px rgba(0,0,0,0.2);
            min-width: 190px;
            padding: 4px 0;
            font-size: 12px;
            color: var(--edt-ctx-text);
        }
        .ctx-item {
            display: flex;
            align-items: center;
            justify-content: space-between;
            padding: 6px 14px;
            cursor: pointer;
            gap: 24px;
            transition: background var(--edt-speed);
        }
        .ctx-item:hover { background: var(--edt-ctx-hover); }
        .ctx-item[data-danger]:hover { background: rgba(239,68,68,0.15); color: #ef4444; }
        .ctx-item .ctx-shortcut { opacity: 0.5; font-size: 11px; }
        .ctx-sep {
            height: 1px;
            background: var(--edt-sep);
            margin: 3px 0;
        }

        /* ── Empty state ──────────────────────────────────────────────── */
        .empty-bar {
            flex: 1;
            display: flex;
            align-items: center;
            padding-left: 12px;
            font-size: 12px;
            color: var(--edt-text);
            font-style: italic;
        }
    `;

    constructor() {
        super();
        this.active = '';
        this.theme = 'light';
        this._tabs = [];    // { key, label, icon, dirty, pinned, preview, closable }
        this._order = [];    // keys in display order
        this._drag = null;  // { key, ghostX, ghostY, overKey, side }
        this._ctxMenu = null;
        this._overflowLeft = false;
        this._overflowRight = false;
        this._everActive = new Set();

        this._onPointerMove = this._onPointerMove.bind(this);
        this._onPointerUp = this._onPointerUp.bind(this);
        this._onClickOutside = this._onClickOutside.bind(this);
        this._onKeyboard = this._onKeyboard.bind(this);
        this._dragPending = null;  // { key, startX, startY, pointerId }
    }

    // ── Lifecycle ────────────────────────────────────────────────────────────

    connectedCallback() {
        super.connectedCallback();
        window.addEventListener('keydown', this._onKeyboard);
        this._readChildren();
    }

    disconnectedCallback() {
        window.removeEventListener('keydown', this._onKeyboard);
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        window.removeEventListener('pointerdown', this._onClickOutside);
        super.disconnectedCallback();
    }

    firstUpdated() {
        this._checkOverflow();
    }

    updated(changed) {
        if (changed.has('active')) {
            this._everActive.add(this.active);
            this._syncPanels();
            this._scrollActiveIntoView();
        }
        this._checkOverflow();
    }

    // ── Read child rd-editor-tab elements ────────────────────────────────────

    _readChildren() {
        const children = [...this.querySelectorAll(':scope > rd-editor-tab')];
        this._tabs = children.map(el => ({
            key: el.getAttribute('key') || '',
            label: el.getAttribute('label') || el.getAttribute('key') || '',
            icon: el.getAttribute('icon') || '',
            dirty: el.hasAttribute('dirty'),
            pinned: el.hasAttribute('pinned'),
            preview: el.hasAttribute('preview'),
            closable: !el.hasAttribute('no-close'),
        }));
        // Build order: pinned first, then rest
        const pinned = this._tabs.filter(t => t.pinned).map(t => t.key);
        const unpinned = this._tabs.filter(t => !t.pinned).map(t => t.key);
        // Merge preserving existing order
        const existing = this._order.filter(k => this._tabs.find(t => t.key === k));
        const newKeys = this._tabs.map(t => t.key).filter(k => !existing.includes(k));
        const merged = [...new Set([...existing, ...newKeys])];
        this._order = [
            ...merged.filter(k => pinned.includes(k)),
            ...merged.filter(k => !pinned.includes(k)),
        ];
        if (!this.active && this._tabs.length) this.active = this._tabs[0].key;
        if (this.active) this._everActive.add(this.active);
        this._syncPanels();
    }

    _syncPanels() {
        const panels = [...this.querySelectorAll(':scope > rd-editor-tab')];
        panels.forEach(p => {
            const k = p.getAttribute('key') || '';
            p.setAttribute('aria-hidden', k !== this.active ? 'true' : 'false');
        });
    }

    // ── Public API ────────────────────────────────────────────────────────────

    /** Add a new tab programmatically */
    addTab({ key, label, icon = '', dirty = false, pinned = false, preview = false, content = '' } = {}) {
        if (!key || this._tabs.find(t => t.key === key)) return;

        // If preview mode: replace existing preview tab
        if (preview) {
            const prevIdx = this._tabs.findIndex(t => t.preview);
            if (prevIdx !== -1) {
                const prevKey = this._tabs[prevIdx].key;
                this.removeTab(prevKey, true);
            }
        }

        const el = document.createElement('rd-editor-tab');
        el.setAttribute('key', key);
        el.setAttribute('label', label || key);
        if (icon) el.setAttribute('icon', icon);
        if (dirty) el.setAttribute('dirty', '');
        if (pinned) el.setAttribute('pinned', '');
        if (preview) el.setAttribute('preview', '');
        if (content) el.innerHTML = content;
        this.appendChild(el);
        this._readChildren();
        this.active = key;
    }

    /** Close/remove a tab */
    removeTab(key, silent = false) {
        const panel = this.querySelector(`rd-editor-tab[key="${key}"]`);
        if (!panel) return;

        const allKeys = this._order.filter(k => this._tabs.find(t => t.key === k));
        const idx = allKeys.indexOf(key);

        panel.remove();
        this._readChildren();

        if (!silent && key === this.active) {
            const remaining = this._order.filter(k => this._tabs.find(t => t.key === k));
            if (remaining.length) {
                this.active = remaining[Math.min(idx, remaining.length - 1)];
            } else {
                this.active = '';
            }
        }

        if (!silent) {
            this.dispatchEvent(new CustomEvent('tab-close', { bubbles: true, composed: true, detail: { key } }));
        }
    }

    /** Mark a tab dirty/clean */
    setDirty(key, dirty = true) {
        const el = this.querySelector(`rd-editor-tab[key="${key}"]`);
        if (!el) return;
        if (dirty) el.setAttribute('dirty', ''); else el.removeAttribute('dirty');
        this._readChildren();
    }

    /** Pin/unpin a tab */
    setPinned(key, pinned = true) {
        const el = this.querySelector(`rd-editor-tab[key="${key}"]`);
        if (!el) return;
        if (pinned) el.setAttribute('pinned', ''); else el.removeAttribute('pinned');
        this._readChildren();
    }

    /** Promote preview tab to permanent */
    promotePreview(key) {
        const el = this.querySelector(`rd-editor-tab[key="${key}"]`);
        if (!el) return;
        el.removeAttribute('preview');
        this._readChildren();
    }

    // ── Selection ────────────────────────────────────────────────────────────

    _selectTab(key) {
        if (key === this.active) return;
        const prev = this.active;
        this.active = key;
        this.dispatchEvent(new CustomEvent('tab-change', {
            bubbles: true, composed: true,
            detail: { key, prev },
        }));
    }

    // ── Keyboard ─────────────────────────────────────────────────────────────

    _onKeyboard(e) {
        // Only when focus is inside this element
        if (!this.contains(document.activeElement) && document.activeElement !== this) return;

        const keys = this._order.filter(k => this._tabs.find(t => t.key === k));
        const idx = keys.indexOf(this.active);

        if (e.key === 'ArrowLeft') {
            e.preventDefault();
            const prev = keys[(idx - 1 + keys.length) % keys.length];
            this._selectTab(prev);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            const next = keys[(idx + 1) % keys.length];
            this._selectTab(next);
        } else if (e.key === 'Home') {
            e.preventDefault();
            if (keys.length) this._selectTab(keys[0]);
        } else if (e.key === 'End') {
            e.preventDefault();
            if (keys.length) this._selectTab(keys[keys.length - 1]);
        } else if (e.key === 'w' && (e.ctrlKey || e.metaKey)) {
            e.preventDefault();
            const tab = this._tabs.find(t => t.key === this.active);
            if (tab && tab.closable && !tab.pinned) this.removeTab(this.active);
        }
    }

    // ── Overflow detection ────────────────────────────────────────────────────

    _checkOverflow() {
        const strip = this.shadowRoot?.querySelector('.tab-strip');
        if (!strip) return;
        const hasLeft = strip.scrollLeft > 2;
        const hasRight = strip.scrollLeft < strip.scrollWidth - strip.clientWidth - 2;
        if (hasLeft !== this._overflowLeft || hasRight !== this._overflowRight) {
            this._overflowLeft = hasLeft;
            this._overflowRight = hasRight;
        }
    }

    _scrollStrip(dir) {
        const strip = this.shadowRoot?.querySelector('.tab-strip');
        if (!strip) return;
        strip.scrollBy({ left: dir * 120, behavior: 'smooth' });
        setTimeout(() => this._checkOverflow(), 200);
    }

    _scrollActiveIntoView() {
        requestAnimationFrame(() => {
            const strip = this.shadowRoot?.querySelector('.tab-strip');
            const btn = this.shadowRoot?.querySelector(`[data-key="${CSS.escape(this.active)}"]`);
            if (strip && btn) {
                const sL = strip.scrollLeft;
                const sW = strip.clientWidth;
                const bL = btn.offsetLeft;
                const bW = btn.offsetWidth;
                if (bL < sL) strip.scrollLeft = bL;
                else if (bL + bW > sL + sW) strip.scrollLeft = bL + bW - sW;
                this._checkOverflow();
            }
        });
    }

    // ── Drag & Drop ──────────────────────────────────────────────────────────

    _onDragStart(e, key) {
        if (e.button !== 0) return;
        const tab = this._tabs.find(t => t.key === key);
        if (!tab) return;

        // Don't activate drag yet — wait for movement threshold in _onPointerMove
        this._dragPending = { key, startX: e.clientX, startY: e.clientY, pointerId: e.pointerId };

        window.addEventListener('pointermove', this._onPointerMove);
        window.addEventListener('pointerup', this._onPointerUp);
    }

    _onPointerMove(e) {
        // ── Threshold activation ────────────────────────────────────────
        if (this._dragPending && !this._drag) {
            const dx = e.clientX - this._dragPending.startX;
            const dy = e.clientY - this._dragPending.startY;
            if (Math.abs(dx) < 5 && Math.abs(dy) < 5) return; // not yet

            // Movement exceeded threshold — start real drag
            const strip = this.shadowRoot?.querySelector('.tab-strip');
            if (strip) {
                try { strip.setPointerCapture(this._dragPending.pointerId); } catch (_) { }
            }
            this._drag = {
                key: this._dragPending.key,
                ghostX: e.clientX - 12,
                ghostY: e.clientY - 14,
                overKey: null,
                side: 'after',
            };
            this._dragPending = null;
        }

        if (!this._drag) return;
        this._drag = { ...this._drag, ghostX: e.clientX - 12, ghostY: e.clientY - 14 };

        // Find which tab we're hovering
        const strip = this.shadowRoot?.querySelector('.tab-strip');
        if (!strip) return;

        const buttons = [...strip.querySelectorAll('.tab')];
        let overKey = null, side = 'after';

        for (const btn of buttons) {
            const r = btn.getBoundingClientRect();
            if (e.clientX >= r.left && e.clientX <= r.right) {
                overKey = btn.dataset.key;
                side = (e.clientX < r.left + r.width / 2) ? 'before' : 'after';
                break;
            }
        }

        this._drag = { ...this._drag, overKey, side };
        this.requestUpdate();
    }

    _onPointerUp() {
        window.removeEventListener('pointermove', this._onPointerMove);
        window.removeEventListener('pointerup', this._onPointerUp);
        this._dragPending = null;  // cancel any pending drag
        if (!this._drag) return;

        const { key, overKey, side } = this._drag;
        this._drag = null;

        if (overKey && overKey !== key) {
            // Reorder
            const order = [...this._order];
            const fromIdx = order.indexOf(key);
            const toIdx = order.indexOf(overKey);
            if (fromIdx !== -1 && toIdx !== -1) {
                order.splice(fromIdx, 1);
                const newTo = order.indexOf(overKey);
                order.splice(side === 'before' ? newTo : newTo + 1, 0, key);
                this._order = order;
                this.dispatchEvent(new CustomEvent('tab-reorder', {
                    bubbles: true, composed: true,
                    detail: { key, order: [...this._order] },
                }));
            }
        }

        this.requestUpdate();
    }

    // ── Context menu ─────────────────────────────────────────────────────────

    _openCtxMenu(e, key) {
        e.preventDefault();
        e.stopPropagation();
        this._ctxMenu = { key, x: e.clientX, y: e.clientY };
        requestAnimationFrame(() => {
            window.addEventListener('pointerdown', this._onClickOutside, { once: true });
        });
        this.requestUpdate();
    }

    _onClickOutside(e) {
        const menu = this.shadowRoot?.querySelector('.ctx-menu');
        if (menu && !menu.contains(e.target)) this._closeCtxMenu();
    }

    _closeCtxMenu() {
        this._ctxMenu = null;
        this.requestUpdate();
    }

    _ctxAction(action) {
        const key = this._ctxMenu?.key;
        this._closeCtxMenu();
        if (!key) return;

        const tab = this._tabs.find(t => t.key === key);
        const keys = this._order.filter(k => this._tabs.find(t => t.key === k));
        const idx = keys.indexOf(key);

        switch (action) {
            case 'close':
                this.removeTab(key);
                break;
            case 'close-others':
                keys.filter(k => k !== key).forEach(k => this.removeTab(k, true));
                this._readChildren();
                this.active = key;
                break;
            case 'close-right':
                keys.slice(idx + 1).forEach(k => this.removeTab(k, true));
                this._readChildren();
                break;
            case 'close-all':
                [...keys].forEach(k => this.removeTab(k, true));
                this._readChildren();
                this.active = '';
                this.dispatchEvent(new CustomEvent('tab-close-all', { bubbles: true, composed: true }));
                break;
            case 'pin':
                this.setPinned(key, !tab?.pinned);
                break;
        }
    }

    // ── Rendering ────────────────────────────────────────────────────────────

    _renderFileIcon(icon) {
        return html`
            <svg class="tab-icon" viewBox="0 0 12 12" style="fill:${fileColor(icon)}">
                <rect x="1" y="0" width="7" height="9" rx="1" opacity="0.25"/>
                <rect x="1" y="0" width="7" height="9" rx="1" fill="none" stroke="${fileColor(icon)}" stroke-width="1"/>
                <path d="M7 0 L10 3 L7 3 Z" fill="${fileColor(icon)}" opacity="0.8"/>
                <line x1="3" y1="5" x2="8" y2="5" stroke="${fileColor(icon)}" stroke-width="0.8" opacity="0.8"/>
                <line x1="3" y1="7" x2="6.5" y2="7" stroke="${fileColor(icon)}" stroke-width="0.8" opacity="0.8"/>
            </svg>
        `;
    }

    _renderTab(key) {
        const tab = this._tabs.find(t => t.key === key);
        if (!tab) return nothing;

        const isDragSource = this._drag?.key === key;
        const isDropBefore = this._drag?.overKey === key && this._drag?.side === 'before';
        const isDropAfter = this._drag?.overKey === key && this._drag?.side === 'after';

        return html`
            <div
                class="tab"
                role="tab"
                tabindex="${tab.key === this.active ? '0' : '-1'}"
                aria-selected="${tab.key === this.active}"
                data-key="${tab.key}"
                ?data-active="${tab.key === this.active}"
                ?data-pinned="${tab.pinned}"
                ?data-preview="${tab.preview}"
                ?data-dirty="${tab.dirty}"
                ?data-dragsource="${isDragSource}"
                ?data-dropbefore="${isDropBefore}"
                ?data-dropafter="${isDropAfter}"
                @click="${() => this._selectTab(tab.key)}"
                @contextmenu="${(e) => this._openCtxMenu(e, tab.key)}"
                @pointerdown="${(e) => { if (e.button === 0) this._onDragStart(e, tab.key); }}"
                @dblclick="${() => tab.preview && this.promotePreview(tab.key)}"
                title="${tab.label}${tab.dirty ? ' ●' : ''}${tab.preview ? ' (preview)' : ''}"
            >
                ${tab.icon ? this._renderFileIcon(tab.icon) : nothing}

                <span class="tab-label">${tab.label}</span>

                <!-- pin icon (always visible on pinned tabs) -->
                <span class="tab-pin-icon" aria-hidden="true">
                    <svg viewBox="0 0 16 16"><path d="M9.828.722a.5.5 0 0 1 .354.146l4.95 4.95a.5.5 0 0 1 0 .707c-.48.48-1.072.588-1.503.588-.177 0-.335-.018-.46-.039l-3.134 3.134a5.927 5.927 0 0 1 .16 1.013c.046.702-.032 1.687-.72 2.375a.5.5 0 0 1-.707 0l-2.829-2.828-3.182 3.182c-.195.195-1.219.902-1.414.707-.195-.195.512-1.22.707-1.414l3.182-3.182-2.828-2.829a.5.5 0 0 1 0-.707c.688-.688 1.673-.767 2.375-.72a5.922 5.922 0 0 1 1.013.16l3.134-3.133a2.772 2.772 0 0 1-.04-.461c0-.43.108-1.022.589-1.503a.5.5 0 0 1 .353-.146z"/></svg>
                </span>

                <!-- close / dirty -->
                ${tab.closable && !tab.pinned ? html`
                    <span class="tab-end">
                        <span class="tab-dirty-dot" aria-hidden="true">●</span>
                        <span class="tab-close" @click="${(e) => { e.stopPropagation(); this.removeTab(tab.key); }}" aria-label="Close ${tab.label}" role="button" tabindex="-1">
                            <span class="tab-close-icon">
                                <svg viewBox="0 0 16 16"><path d="M4.646 4.646a.5.5 0 0 1 .708 0L8 7.293l2.646-2.647a.5.5 0 0 1 .708.708L8.707 8l2.647 2.646a.5.5 0 0 1-.708.708L8 8.707l-2.646 2.647a.5.5 0 0 1-.708-.708L7.293 8 4.646 5.354a.5.5 0 0 1 0-.708z"/></svg>
                            </span>
                        </span>
                    </span>
                ` : nothing}
            </div>
        `;
    }

    _renderDragGhost() {
        if (!this._drag) return nothing;
        const tab = this._tabs.find(t => t.key === this._drag.key);
        if (!tab) return nothing;
        return html`
            <div class="drag-ghost" style="left:${this._drag.ghostX}px;top:${this._drag.ghostY}px;">
                ${tab.icon ? this._renderFileIcon(tab.icon) : nothing}
                <span>${tab.label}</span>
            </div>
        `;
    }

    _renderCtxMenu() {
        if (!this._ctxMenu) return nothing;
        const { key, x, y } = this._ctxMenu;
        const tab = this._tabs.find(t => t.key === key);
        const keys = this._order.filter(k => this._tabs.find(t => t.key === k));
        const idx = keys.indexOf(key);
        const isLast = idx === keys.length - 1;
        const isOnly = keys.length === 1;

        return html`
            <div class="ctx-menu" style="left:${x}px;top:${y}px;" @click="${e => e.stopPropagation()}">
                <div class="ctx-item" @click="${() => this._ctxAction('close')}">
                    Close <span class="ctx-shortcut">Ctrl+W</span>
                </div>
                <div class="ctx-item" @click="${() => this._ctxAction('close-others')}"
                     style="${isOnly ? 'opacity:0.4;pointer-events:none' : ''}">
                    Close Others
                </div>
                <div class="ctx-item" @click="${() => this._ctxAction('close-right')}"
                     style="${isLast ? 'opacity:0.4;pointer-events:none' : ''}">
                    Close to the Right
                </div>
                <div class="ctx-item" data-danger @click="${() => this._ctxAction('close-all')}">
                    Close All
                </div>
                <div class="ctx-sep"></div>
                <div class="ctx-item" @click="${() => this._ctxAction('pin')}">
                    ${tab?.pinned ? 'Unpin Tab' : 'Pin Tab'}
                </div>
            </div>
        `;
    }

    render() {
        const orderedTabs = this._order.filter(k => this._tabs.find(t => t.key === k));

        return html`
            <div class="bar-wrapper">
                <button class="scroll-btn" ?hidden="${!this._overflowLeft}"
                        @click="${() => this._scrollStrip(-1)}" aria-label="Scroll tabs left" tabindex="-1">
                    <svg viewBox="0 0 16 16"><path d="M11.354 1.646a.5.5 0 0 1 0 .708L5.707 8l5.647 5.646a.5.5 0 0 1-.708.708l-6-6a.5.5 0 0 1 0-.708l6-6a.5.5 0 0 1 .708 0z"/></svg>
                </button>

                <div
                    class="tab-strip"
                    role="tablist"
                    aria-label="Editor tabs"
                    @scroll="${() => this._checkOverflow()}"
                >
                    ${orderedTabs.length
                ? orderedTabs.map(k => this._renderTab(k))
                : html`<span class="empty-bar">No open files</span>`
            }
                </div>

                <button class="scroll-btn" ?hidden="${!this._overflowRight}"
                        @click="${() => this._scrollStrip(1)}" aria-label="Scroll tabs right" tabindex="-1">
                    <svg viewBox="0 0 16 16"><path d="M4.646 1.646a.5.5 0 0 1 .708 0l6 6a.5.5 0 0 1 0 .708l-6 6a.5.5 0 0 1-.708-.708L10.293 8 4.646 2.354a.5.5 0 0 1 0-.708z"/></svg>
                </button>
            </div>

            <div class="panel-wrap" role="presentation">
                <slot @slotchange="${this._readChildren}"></slot>
            </div>

            ${this._renderDragGhost()}
            ${this._renderCtxMenu()}
        `;
    }
}

customElements.define('rd-editor-tabs', RDEditorTabs);


// ─── rd-editor-tab ───────────────────────────────────────────────────────────
//  Child panel element for rd-editor-tabs.
//  Attributes: key, label, icon, dirty, pinned, preview, no-close
// ─────────────────────────────────────────────────────────────────────────────

export class RDEditorTab extends LitElement {
    static properties = {
        key: { type: String, reflect: true },
        label: { type: String, reflect: true },
        icon: { type: String, reflect: true },
        dirty: { type: Boolean, reflect: true },
        pinned: { type: Boolean, reflect: true },
        preview: { type: Boolean, reflect: true },
    };

    static styles = css`
        :host {
            display: block;
            width: 100%;
            height: 100%;
            overflow: auto;
        }
        :host([aria-hidden="true"]) { display: none; }
    `;

    constructor() {
        super();
        this.key = '';
        this.label = '';
        this.icon = '';
        this.dirty = false;
        this.pinned = false;
        this.preview = false;
    }

    render() {
        return html`
            <div role="tabpanel" id="panel-${this.key}" aria-labelledby="tab-${this.key}" tabindex="0"
                 style="width:100%;height:100%;outline:none;">
                <slot></slot>
            </div>
        `;
    }
}

customElements.define('rd-editor-tab', RDEditorTab);


// ─── Updated convenience API ──────────────────────────────────────────────────

/**
 * Programmatically add a tab to an rd-editor-tabs element.
 * @param {string|HTMLElement} id
 * @param {object} options
 */
export function addEditorTab(id, options) {
    const el = typeof id === 'string' ? document.getElementById(id) : id;
    if (el?.addTab) el.addTab(options);
}

/**
 * Close a tab on an rd-editor-tabs element.
 * @param {string|HTMLElement} id
 * @param {string} key
 */
export function closeEditorTab(id, key) {
    const el = typeof id === 'string' ? document.getElementById(id) : id;
    if (el?.removeTab) el.removeTab(key);
}

window.addEditorTab = addEditorTab;
window.closeEditorTab = closeEditorTab;
